import { GetQueryParams, GetQueryResponse } from '../../../types/api.type';
import {
  PayrollResponse,
  timesheetOverride,
  TimesheetOverrideResponse,
  timesheetOverrideUpdateFormValues
} from '../../../types/finance.types';
import { sfcApi } from '../../api';

type CreateTimesheetOverridePayload = timesheetOverride & {
  lesson_id: number;
  instructor_id: number;
  timesheet_override_type?: number;
  override_request?: string;
};

export const timesheetsApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentEntries: builder.query<PayrollResponse, number>({
      query: (id) => ({
        url: `current-entries${id ? '/' + id : ''}`
      }),
      providesTags: ['CurrentEntries']
    }),
    getTimesheetOverrides: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `timesheet-overrides/${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      }),
      providesTags: ['TimesheetOverrides']
    }),
    createTimesheetOverride: builder.mutation<
      TimesheetOverrideResponse,
      CreateTimesheetOverridePayload
    >({
      query: (params) => ({
        method: 'POST',
        url: `timesheet-overrides/create`,
        body: params
      }),
      invalidatesTags: ['TimesheetOverrides'],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const lessonId = Number(arg.lesson_id);
        const instructorId = Number(arg.instructor_id);
        if (!lessonId || !instructorId) return;

        const optimisticOverride = {
          id: -1,
          timesheet_override_type: Number(arg.timesheet_override_type) || undefined,
          override_request: arg.override_request,
          override_approved: null,
          denial_notes: null
        };

        const patchResults: { undo: () => void }[] = [];
        const patchedQueryArgs = new Set<number>();

        const patchQuery = (queryArg: number, override: object) => {
          if (patchedQueryArgs.has(queryArg)) return;
          patchedQueryArgs.add(queryArg);
          patchResults.push(
            dispatch(
              timesheetsApi.util.updateQueryData('getCurrentEntries', queryArg, (draft) => {
                const entry = draft.payroll?.timesheetEntries?.find(
                  (e) =>
                    Number(e.lesson_id) === lessonId && Number(e.instructor_id) === instructorId
                );
                if (!entry || (Number((override as { id?: number }).id) === -1 && entry.override?.id))
                  return;
                entry.override = override;
              })
            )
          );
        };

        patchQuery(instructorId, optimisticOverride);

        for (const { endpointName, originalArgs } of timesheetsApi.util.selectInvalidatedBy(
          getState(),
          [{ type: 'CurrentEntries' }]
        )) {
          if (endpointName !== 'getCurrentEntries') continue;
          patchQuery(Number(originalArgs), optimisticOverride);
        }

        try {
          const { data } = await queryFulfilled;
          if (!data?.timesheetOverride) return;

          patchedQueryArgs.clear();
          patchQuery(instructorId, data.timesheetOverride);

          for (const { endpointName, originalArgs } of timesheetsApi.util.selectInvalidatedBy(
            getState(),
            [{ type: 'CurrentEntries' }]
          )) {
            if (endpointName !== 'getCurrentEntries') continue;
            patchQuery(Number(originalArgs), data.timesheetOverride);
          }
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      }
    }),
    updateTimesheetOverride: builder.mutation<
      TimesheetOverrideResponse,
      Partial<timesheetOverrideUpdateFormValues>
    >({
      query: (data) => ({
        url: `timesheet-overrides/update/${data.override_id}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['TimesheetOverrides', 'CurrentEntries'],
      async onQueryStarted({ override_id }, { dispatch, queryFulfilled, getState }) {
        if (!override_id) return;

        const patchResults: { undo: () => void }[] = [];
        const cachedQueries = timesheetsApi.util.selectInvalidatedBy(getState(), [
          { type: 'TimesheetOverrides' }
        ]);

        for (const { endpointName, originalArgs } of cachedQueries) {
          if (endpointName !== 'getTimesheetOverrides') continue;
          patchResults.push(
            dispatch(
              timesheetsApi.util.updateQueryData(
                'getTimesheetOverrides',
                originalArgs as GetQueryParams | void,
                (draft) => {
                  if (!draft.results?.length) return;
                  const nextResults = draft.results.filter(
                    (row: { id: number }) => row.id !== override_id
                  );
                  if (nextResults.length === draft.results.length) return;
                  draft.results = nextResults;
                  if (typeof draft.count === 'number' && draft.count > 0) {
                    draft.count -= 1;
                  }
                  if (draft.scope_counts?.pending != null && draft.scope_counts.pending > 0) {
                    draft.scope_counts.pending -= 1;
                  }
                }
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      }
    }),
    getTimesheets: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `timesheets/`,
        method: 'POST',
        body: params?.data
      })
    }),
    getTimesheetEntries: builder.query<
      GetQueryResponse,
      { timesheetId: number; filters: GetQueryParams | void }
    >({
      query: (params) => ({
        url: `timesheet-entries/${params.timesheetId}`,
        method: 'POST',
        body: params?.filters?.data
      })
    })
  })
});

export const {
  useGetCurrentEntriesQuery,
  useGetTimesheetOverridesQuery,
  useCreateTimesheetOverrideMutation,
  useUpdateTimesheetOverrideMutation,
  useGetTimesheetsQuery,
  useGetTimesheetEntriesQuery
} = timesheetsApi;
